/**
 * useFilterPersistence Hook
 *
 * Manages filter persistence for library and gallery pages.
 * Follows the same pattern as layout persistence in LayoutIcons.
 *
 * Usage:
 *   const { persistCurrentFilters } = useFilterPersistence(VIEW_CONTEXT.LIBRARY);
 *   const { persistCurrentFilters } = useFilterPersistence(VIEW_CONTEXT.GALLERY, galleryId);
 */

import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { DEFAULT_SIZE, VIEW_CONTEXT } from 'src/constants';
import type { ViewStateForFilters } from 'src/helpers/filterPersistence';
import {
  buildFilterStateFromViewState,
  clearFilters,
  filterValuesToChips,
  getPersistedFilters,
  hasUrlFilters,
  isValidFilterState,
  persistFilters,
} from 'src/helpers/filterPersistence';
import viewStateVar from 'src/store/viewState';
import type { PaginationState, ViewState } from 'src/types/store.types';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

// View context type
type ViewContextType = (typeof VIEW_CONTEXT)[keyof typeof VIEW_CONTEXT];

// Filter value structure for persistence
interface FilterValue {
  category: string;
  choice: string;
  value: string;
}

// Filter chip structure for viewState
interface FilterChip {
  category: string;
  choice: string;
  chip: string;
}

// Persisted filter state structure
interface PersistedFilterState {
  searchTerm?: string;
  filterValues?: FilterValue[];
  sort?: string | null;
  scope?: string;
  page?: number;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  } | null;
  version?: number;
  savedAt?: number | null;
}

// Return type for useFilterPersistence
interface UseFilterPersistenceReturn {
  persistCurrentFilters: () => void;
  clearPersistedFilters: () => void;
}

/**
 * Hook for managing filter persistence
 * @param viewContext - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery view context)
 * @returns Persistence functions
 */
export const useFilterPersistence = (
  viewContext: ViewContextType = VIEW_CONTEXT.LIBRARY,
  galleryId: string | null = null,
): UseFilterPersistenceReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const hasRestoredRef = useRef<boolean>(false);
  const restorationCompleteRef = useRef<boolean>(false);

  /**
   * Restore filters from localStorage if URL has no filters
   * Called once on mount
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.search intentionally excluded to prevent re-running on URL changes during restoration
  useEffect(() => {
    // Only restore once per mount
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    // Convert search object to URLSearchParams format
    const searchObj = location.search as Record<string, unknown>;
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(searchObj)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          for (const v of value) {
            urlParams.append(key, String(v));
          }
        } else {
          urlParams.set(key, String(value));
        }
      }
    }

    // If URL already has filters, they are source of truth — skip localStorage restore.
    // Library UI components (SearchAppBar, FilterWidget, SortBy) already sync from URL on mount.
    if (hasUrlFilters(urlParams)) {
      restorationCompleteRef.current = true;
      return;
    }

    // Try to restore from localStorage
    const persisted = getPersistedFilters(viewContext, galleryId) as PersistedFilterState | null;

    if (!persisted || !isValidFilterState(persisted)) {
      // Mark restoration as complete even if nothing to restore
      restorationCompleteRef.current = true;
      return;
    }

    // Restore to viewState
    const currentViewState = viewStateVar();
    const updates: Partial<ViewState> = {};

    if (persisted.searchTerm) {
      updates.searchTerm = persisted.searchTerm;
    }

    if (persisted.filterValues && persisted.filterValues.length > 0) {
      // Note: slugToFieldMeta is not available here (field metadata is loaded by FilterWidget).
      // These chips use the abbreviated fallback style briefly until FilterWidget mounts and
      // rebuilds them with full admin-configured style via addFiltersFromURL.
      updates.filterChips = filterValuesToChips(persisted.filterValues) as FilterChip[];
      updates.filterCategoryExclude = persisted.filterValues.map((f: FilterValue) => f.category);
    }

    if (persisted.page && persisted.page > 1) {
      updates.pagination = {
        ...currentViewState.pagination,
        page: persisted.page,
      } as PaginationState;
    }

    // Only update if we have something to restore
    if (Object.keys(updates).length > 0) {
      viewStateVar({
        ...currentViewState,
        ...updates,
      });

      // Project restored state to URL using TanStack Router navigate
      const newSearch: Record<string, unknown> = {};
      if (persisted.searchTerm) {
        newSearch.term = persisted.searchTerm;
      }
      if (persisted.filterValues && persisted.filterValues.length > 0) {
        newSearch.filters = persisted.filterValues.map((f: FilterValue) => f.value);
      }
      if (persisted.sort) {
        newSearch.sort = persisted.sort;
      }
      // Always set pagination params
      newSearch.page = persisted.page || 1;
      newSearch.size = DEFAULT_SIZE;

      if (persisted.scope && persisted.scope !== 'all' && viewContext === VIEW_CONTEXT.LIBRARY) {
        newSearch.scope = persisted.scope;
      }

      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          ...newSearch,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });

      // Mark restoration complete after URL update settles
      // This prevents auto-persist from firing during restoration
      setTimeout(() => {
        restorationCompleteRef.current = true;
      }, 150);
    } else {
      restorationCompleteRef.current = true;
    }
  }, [viewContext, galleryId, navigate]);

  /**
   * Persist current filter state to localStorage
   */
  const persistCurrentFilters = useCallback((): void => {
    const viewState = viewStateVar();
    const filterState = buildFilterStateFromViewState(viewState as ViewStateForFilters);

    // Use URL search params as source of truth for page - viewState.pagination.page
    // is not synced from URL, so we need to read directly from TanStack Router's search
    if (search.page !== undefined && search.page !== null) {
      filterState.page = search.page;
    }

    // Also capture scope from URL if on library
    // Use the search object from TanStack Router's useSearch instead of window.location.search
    if (viewContext === VIEW_CONTEXT.LIBRARY && search.scope) {
      filterState.scope = search.scope;
    }

    persistFilters(viewContext, galleryId, filterState);
  }, [viewContext, galleryId, search.scope, search.page]);

  /**
   * Auto-persist when URL changes (filters/search/page/term changed)
   * Wait for restoration to complete before auto-persisting
   */
  // Stable string key that changes when any search param changes
  const searchKey = `${search.term || ''}|${(search.filters || []).join(',')}|${search.sort || ''}|${search.page || ''}|${search.scope || ''}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchKey covers all relevant search param changes
  useEffect(() => {
    // Don't auto-persist until restoration is complete
    // This prevents the restored state from immediately being re-persisted
    if (!restorationCompleteRef.current) {
      return;
    }

    // Debounce persistence slightly to avoid rapid writes
    const timeoutId = setTimeout(() => {
      persistCurrentFilters();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [persistCurrentFilters, searchKey]);

  /**
   * Clear persisted filters for this view context
   */
  const clearPersistedFilters = useCallback((): void => {
    clearFilters(viewContext, galleryId);
  }, [viewContext, galleryId]);

  return {
    persistCurrentFilters,
    clearPersistedFilters,
  };
};

export default useFilterPersistence;
